<template>
  <div class="face-fort-biometry">
    <!-- Erro -->
    <div v-if="status === 'error'" class="error-state">
      <p class="error-text">{{ errorMessage }}</p>
      <div class="error-buttons">
        <button class="btn btn-primary mt-3" @click="retry">Tentar novamente</button>
      </div>
    </div>

    <!-- Container Fortface: facial e documento -->
    <div
      v-show="status === 'capture' || status === 'document_loading' || status === 'document_capture'"
      ref="sdkContainer"
      class="sdk-container"
      :class="{ 'sdk-container--fullscreen': status === 'document_capture' }"
    >
      <fortface-sdk v-if="sdkReady" ref="fortfaceSdk" class="fortface-sdk"></fortface-sdk>
    </div>

    <!-- Popup: loading, envio, sucesso/erro; documento também no popup, popup quase tela toda -->
    <div v-if="showLoadingPopup" class="modal-overlay">
      <div class="modal-content modal-content--large" :style="modalContentStyle">
        <!-- Loading: 3 bolinhas + texto (troca conforme o estado) -->
        <div v-if="isLoadingState" class="modal-loading">
          <p class="modal-message modal-loading-text">{{ loadingPopupText }}</p>
          <span
            class="bounce-dots"
            :style="{ '--dot-color': (template.primaryBtn && template.primaryBtn.bgColor) || '#333' }"
          >
            <span class="bounce-dot"></span>
            <span class="bounce-dot"></span>
            <span class="bounce-dot"></span>
          </span>
        </div>
        <!-- Sucesso facial: opção de fazer biometria de documentos ou continuar -->
        <template v-else-if="captureModalState === 'success'">
          <p class="modal-title modal-title--extra-large" :style="{ color: template.primaryColor || '#28a745' }">Biometria facial registrada com sucesso</p>
          <p class="modal-message">Sua biometria facial foi cadastrada.</p>
          <p class="modal-message">Deseja também fazer a biometria de documentos?</p>
          <div class="modal-buttons modal-buttons--stacked">
            <button type="button" class="btn btn-primary mt-3" style="font-size: 1.05rem; width: 500px;" :style="primaryBtnStyle" @click="onStartDocumentBiometry">Biometria de documentos</button>
            <button type="button" class="btn btn-primary mt-1" style="font-size: 1.05rem; width: 500px; background-color: #003F20;" :style="primaryBtnStyle" @click="onSuccessContinue">Continuar</button>
          </div>
        </template>
        <!-- Sucesso documento (frente → segue direto para verso; verso → Continuar) -->
        <template v-else-if="captureModalState === 'document_success' && documentSuccessSide === 'verso'">
          <p class="modal-title modal-title--large" :style="{ color: template.secondaryColor || '#28a745' }">Documento registrado com sucesso</p>
          <p class="modal-message">Biometria de documentos concluída.</p>
          <button type="button" class="btn btn-primary mt-3" :style="primaryBtnStyle" @click="onSuccessContinue">Continuar</button>
        </template>
        <!-- Erro: informa e permite tentar de novo -->
        <template v-else-if="captureModalState === 'error'">
          <p class="modal-title" :style="{ color: template.inputBorderColor || template.secondaryColor || '#dc3545' }">Erro na leitura</p>
          <p class="modal-message">{{ captureErrorMessage }}</p>
          <button type="button" class="btn btn-primary mt-3" :style="primaryBtnStyle" @click="onErrorTryAgain">Tentar novamente</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getSSORedirectUrl } from '@/helpers/ssoHelper'
import { loginFB } from '@/services/ServFB'

const lFB = new loginFB()

const SDK_SCRIPT_URL =
  process.env.VUE_APP_FORTFACE_SDK_URL ||
  `${process.env.BASE_URL || '/'}static/assets/fortface-sdk/fortface-sdk.esm.js`

export default {
  name: 'FaceFortBiometry',
  data: () => ({
    status: 'loading', // loading | init | capture | error | done
    errorMessage: '',
    sdkReady: false,
    deviceRequestInfo: null,
    sessionId: null,
    sessionKey: null,
    sessionToken: null,
    token: null,
    showCaptureModal: false,
    captureModalState: 'loading', // 'loading' | 'success' | 'error' | 'document_success'
    captureErrorMessage: '',
    captureRequestInProgress: false,
    documentSuccessSide: null, // 'frente' | 'verso' após sucesso do documento
    documentSide: 'frente', // qual face estamos capturando: frente ou verso
    documentRequestInProgress: false,
    isDocumentFlow: false,
  }),
  computed: {
    ...mapState(['config', 'template', 'registrationObject', 'identity']),
    ...mapGetters(['primaryBtnStyle']),
    goToHomePage() {
      return getSSORedirectUrl(this.config)
    },
    modalContentStyle() {
      const bg = '#fff'
      const color = '#333'
      return { background: bg, color }
    },
    continuarLinkStyle() {
      const color = (this.template && this.template.primaryColor) ? this.template.primaryColor : '#333'
      return { color, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }
    },
    showLoadingPopup() {
      return (this.status === 'loading' || this.status === 'init' || this.status === 'document_loading') || this.showCaptureModal
    },
    isLoadingState() {
      return (this.status === 'loading' || this.status === 'init' || this.status === 'document_loading') || this.captureModalState === 'loading'
    },
    loadingPopupText() {
      if (this.status === 'document_loading' && this.documentSide === 'verso') return 'Preparando para ler o verso'
      if (this.status === 'document_loading') return 'Preparando captura de documento...'
      if (this.status === 'loading' || this.status === 'init') return 'Estamos carregando sua tela de biometria'
      if (this.isDocumentFlow && this.showCaptureModal && this.captureModalState === 'loading') return 'Enviando documento...'
      return 'Enviando biometria'
    },
  },
  async mounted() {
    if (typeof window === 'undefined' || !window.customElements) {
      this.setError('Ambiente não suportado para biometria.')
      return
    }
    await this.runFlow()
  },
  methods: {
    setError(msg) {
      this.status = 'error'
      this.errorMessage = msg
    },
    async runFlow() {
      try {
        this.status = 'loading'
        await this.loadSdkScript()
        this.sdkReady = true
        await this.$nextTick()
        await this.ensureToken()
        const deviceRequestInfo = await this.runSdkStart()
        this.deviceRequestInfo = deviceRequestInfo
        const { sessionId, sessionKey, sessionToken } = await lFB.fortfaceHandshake(
          deviceRequestInfo,
          this.token
        )
        this.sessionId = sessionId
        this.sessionKey = sessionKey
        this.sessionToken = sessionToken
        this.status = 'init'
        await this.startSession()
      } catch (err) {
        const msg = typeof err === 'string' ? err : (err && err.message) || 'Erro ao carregar biometria.'
        this.setError(msg)
      }
    },
    loadSdkScript() {
      return new Promise((resolve, reject) => {
        if (document.querySelector('script[data-fortface-sdk]')) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.type = 'module'
        script.src = SDK_SCRIPT_URL
        script.setAttribute('data-fortface-sdk', 'true')
        script.onload = () => resolve()
        script.onerror = () =>
          reject(
            new Error(
              'Não foi possível carregar o SDK Fortface. Verifique se o arquivo está em public/static/assets/fortface-sdk/'
            )
          )
        document.head.appendChild(script)
      })
    },
    async ensureToken() {
      const session = localStorage.getItem('session')
      if (session) {
        this.token = session
        return
      }
      // Mesmo critério do Login quando vem do cadastro: login = cpf ou email conforme config
      const loginId =
        this.registrationObject &&
        (this.config.loginType == 1
          ? this.registrationObject.cpf
          : this.registrationObject.email)
      const hasPassword =
        this.registrationObject && this.registrationObject.password != null

      if (loginId != null && hasPassword) {
        const payload = {
          id: loginId,
          password: this.registrationObject.password,
          loginType: parseInt(this.config.loginType, 10),
          systemId: parseInt(this.config.systemID, 10),
          prjId: parseInt(this.config.prj, 10),
          ownerId: parseInt(this.config.ownerId, 10),
          alternativeLogin: (this.registrationObject && this.registrationObject.alternativeLogin) || false,
          subDomainLogin: this.config.name || '',
          alternativeNumber: (this.registrationObject && this.registrationObject.alternativeNumber) || '1',
        }
        const data = await lFB.login(payload)
        this.token = data
        if (this.token) localStorage.setItem('session', this.token)
        return
      }
      throw new Error('É necessário estar logado para realizar a biometria.')
    },
    async runSdkStart() {
      await customElements.whenDefined('fortface-sdk')
      const el = this.$refs.fortfaceSdk || document.querySelector('fortface-sdk')
      if (!el || typeof el.start !== 'function') {
        throw new Error('SDK Fortface não inicializado.')
      }
      const deviceRequestInfo = await el.start()
      return deviceRequestInfo
    },
    startSession() {
      const el = this.$refs.fortfaceSdk || document.querySelector('fortface-sdk')
      if (!el || typeof el.startSession !== 'function') {
        this.setError('SDK Fortface não pronto para captura.')
        return
      }
      this.status = 'capture'
      el.startSession(
        this.fortfaceFinishSession.bind(this),
        this.sessionId,
        this.sessionKey,
        { returnMetrics: true }
      )
    },
    fortfaceFinishSession(result) {
      const { action, data, sessionDetails } = result
      switch (action) {
        case 'capture':
          this.handleCapture(data, sessionDetails)
          break
        case 'cancel':
        case 'timeout':
        case 'timeout_ready':
          this.handleCancelOrTimeout(data, sessionDetails)
          break
        case 'error':
          this.handleSessionError(data, sessionDetails)
          break
        default:
          this.finish()
      }
    },
    async handleCapture(data, sessionDetails) {
      if (!data || !data.encryptData) {
        this.finish()
        return
      }
      const clientId = this.config && this.config.name
      const externalUserId =
        (this.identity && this.identity.profile && this.identity.profile.cpf) ||
        (this.identity && this.identity.document) ||
        (this.registrationObject && this.registrationObject.cpf) ||
        ''
      if (!clientId || !externalUserId) {
        if (this.$bvToast && this.$bvToast.toast) {
          this.$bvToast.toast('Configuração ou documento do usuário não encontrado.', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        }
        this.finish()
        return
      }
      if (this.captureRequestInProgress) return
      this.captureRequestInProgress = true
      this.showCaptureModal = true
      this.captureModalState = 'loading'
      try {
        await lFB.fortfaceCapture(
          externalUserId,
          data,
          sessionDetails,
          this.sessionKey,
          this.sessionToken,
          this.deviceRequestInfo,
          this.token
        )
        this.status = 'done'
        this.captureModalState = 'success'
      } catch (err) {
        this.captureErrorMessage = typeof err === 'string' ? err : (err && err.message) || 'Falha ao enviar captura.'
        this.captureModalState = 'error'
      } finally {
        this.captureRequestInProgress = false
      }
    },
    onSuccessContinue() {
      this.showCaptureModal = false
      this.captureModalState = 'loading'
      this.documentSuccessSide = null
      this.isDocumentFlow = false
      this.finish()
    },
    onStartDocumentBiometry() {
      this.documentSide = 'frente'
      this.documentSuccessSide = null
      this.isDocumentFlow = true
      this.captureModalState = 'loading'
      this.status = 'document_loading'
      this.runDocumentFlow()
    },
    async runDocumentFlow() {
      try {
        await this.ensureToken()
        await this.$nextTick()
        await customElements.whenDefined('fortface-sdk')
        const el = this.$refs.fortfaceSdk || document.querySelector('fortface-sdk')
        if (!el || typeof el.startDoc !== 'function') {
          this.setError('SDK Fortface não suporta captura de documento.')
          return
        }
        const deviceRequestInfo = await el.startDoc()
        this.deviceRequestInfo = deviceRequestInfo
        const { sessionId, sessionKey, sessionToken } = await lFB.fortfaceHandshake(
          deviceRequestInfo,
          this.token
        )
        this.sessionId = sessionId
        this.sessionKey = sessionKey
        this.sessionToken = sessionToken
        this.status = 'document_capture'
        this.showCaptureModal = false
        if (typeof el.startSessionDoc !== 'function') {
          this.setError('SDK Fortface não suporta startSessionDoc.')
          return
        }
        el.startSessionDoc(
          this.fortfaceFinishSessionDoc.bind(this),
          this.sessionId,
          this.sessionKey
        )
      } catch (err) {
        const msg = typeof err === 'string' ? err : (err && err.message) || 'Erro ao preparar captura de documento.'
        this.setError(msg)
      }
    },
    fortfaceFinishSessionDoc(result) {
      const { action, data, sessionDetails } = result
      switch (action) {
        case 'capture':
          this.handleDocumentCapture(data, sessionDetails)
          break
        case 'cancel':
        case 'timeout':
        case 'timeout_ready':
          this.handleDocumentCancelOrTimeout(data, sessionDetails)
          break
        case 'error':
          this.handleDocumentSessionError(data, sessionDetails)
          break
        default:
          this.finish()
      }
    },
    handleDocumentCapture(data, sessionDetails) {
      if (!data || !data.encryptData) {
        this.finish()
        return
      }
      // Por enquanto sem POST: considerar sucesso assim que o SDK ler o documento
      if (this.documentSide === 'frente') {
        this.documentSide = 'verso'
        this.status = 'document_loading'
        this.showCaptureModal = true
        this.captureModalState = 'loading'
        this.runDocumentFlow()
        return
      }
      this.status = 'done'
      this.showCaptureModal = true
      this.captureModalState = 'document_success'
      this.documentSuccessSide = 'verso'
    },
    handleDocumentCancelOrTimeout(data, sessionDetails) {
      this.status = 'done'
      this.finish()
    },
    handleDocumentSessionError(data, sessionDetails) {
      const code = sessionDetails && sessionDetails.errorCode
      const msg =
        code === 'cameraPermissionDenied'
          ? 'Permissão de câmera negada.'
          : code === 'noCompatibleVideoDevice'
            ? 'Nenhuma câmera compatível encontrada.'
            : `Erro na captura do documento (${code || 'unknown'}).`
      this.setError(msg)
    },
    onErrorTryAgain() {
      this.showCaptureModal = false
      this.captureModalState = 'loading'
      this.captureErrorMessage = ''
      this.captureRequestInProgress = false
      if (this.isDocumentFlow) {
        this.documentRequestInProgress = false
        this.runDocumentFlow()
      } else {
        this.startSession()
      }
    },
    handleCancelOrTimeout(data, sessionDetails) {
      this.status = 'done'
      this.finish()
    },
    handleSessionError(data, sessionDetails) {
      const code = (sessionDetails && sessionDetails.errorCode) || 'unknown'
      const msg =
        code === 'cameraPermissionDenied'
          ? 'Permissão de câmera negada.'
          : code === 'noCompatibleVideoDevice'
            ? 'Nenhuma câmera compatível encontrada.'
            : `Erro na captura (${code}).`
      this.setError(msg)
    },
    retry() {
      this.status = 'loading'
      this.errorMessage = ''
      this.runFlow()
    },
    finish() {
      this.$router.push(this.goToHomePage)
    },
  },
}
</script>

<style scoped>
.face-fort-biometry {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.error-state {
  text-align: center;
  color: #fff;
}
.error-text {
  color: #f8d7da;
}
.error-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
}
.sdk-container {
  width: 100%;
  min-height: 320px;
  position: relative;
}
.sdk-container--fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  z-index: 500;
  background: #000;
}
.sdk-container--fullscreen .fortface-sdk {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100%;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.modal-content {
  border-radius: 12px;
  padding: 1.5rem 2rem;
  max-width: 360px;
  width: 100%;
  text-align: center;
}
.modal-content--large {
  width: 61vw;
  max-width: 61vw;
  min-height: 59vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.modal-content .modal-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.modal-title--large {
  font-size: 1.5rem;
}

.modal-title--extra-large {
  font-size: 2rem;
}

.modal-content .modal-message {
  font-size: 0.95rem;
  margin-bottom: 1rem;
  opacity: 0.9;
}
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 0;
  min-height: 80px;
}
.modal-loading .modal-loading-text {
  margin: 0 0 1.25rem 0;
  font-size: 1rem;
  color: #333;
  text-align: center;
}
.bounce-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.bounce-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--dot-color, #333);
  animation: bounce-dot 0.6s ease-in-out infinite both;
}
.bounce-dot:nth-child(1) { animation-delay: 0s; }
.bounce-dot:nth-child(2) { animation-delay: 0.15s; }
.bounce-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes bounce-dot {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-6px); }
}
.modal-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 0.5rem;
}
.modal-buttons--stacked {
  flex-direction: column;
  align-items: center;
}
.modal-buttons--stacked .btn {
  width: 100%;
  max-width: 280px;
}
.modal-content .btn-primary {
  min-width: 140px;
}
.btn-link-continuar {
  min-width: auto;
  font-size: 1rem;
}
.btn-link-continuar:hover {
  opacity: 0.85;
}
</style>
